/**
 * Performance Monitoring
 * Tracks Web Vitals and custom performance metrics
 *
 * Auth-aware: Metrics are buffered until user is authenticated,
 * then flushed to Firestore. This prevents permission errors on
 * unauthenticated routes like /login.
 */

import { onCLS, onINP, onLCP, onFCP, onTTFB, type Metric } from 'web-vitals';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '@/lib/firebase';
import { logger } from '@/lib/logger';

interface PerformanceMetrics {
  lcp?: number;
  fid?: number;
  cls?: number;
  fcp?: number;
  ttfb?: number;
}

interface BufferedMetric {
  name: string;
  value: number;
  rating: string;
  delta: number;
  id: string;
  timestamp: number;
}

const metrics: PerformanceMetrics = {};

// Buffer for metrics collected before auth is available
const metricBuffer: BufferedMetric[] = [];
const MAX_BUFFER_SIZE = 20;
const BUFFER_TTL_MS = 60_000; // 60 seconds

// Track if we've already flushed (to avoid duplicate flushes)
let hasFlushed = false;

/**
 * Check if user is authenticated
 */
function isAuthenticated(): boolean {
  try {
    const auth = getAuth();
    return !!auth.currentUser;
  } catch {
    return false;
  }
}

/**
 * Write a metric to Firestore (internal, assumes auth check already done)
 */
function writeMetricToFirestore(metric: BufferedMetric): void {
  addDoc(collection(db, 'systemMetrics'), {
    type: 'performance',
    name: metric.name,
    value: metric.value,
    unit: 'ms',
    rating: metric.rating,
    timestamp: Timestamp.fromMillis(metric.timestamp),
    metadata: {
      delta: metric.delta,
      id: metric.id,
    },
  }).catch((err) => {
    // Only log if it's not a permission error (which shouldn't happen now)
    const errorMessage = err instanceof Error ? err.message : String(err);
    if (!errorMessage.includes('permission')) {
      logger.error('[Performance] Failed to log metric to Firestore:', err);
    }
  });
}

/**
 * Flush buffered metrics to Firestore
 * Called when auth becomes available
 */
export function flushPerformanceMetrics(): void {
  if (hasFlushed || metricBuffer.length === 0) return;
  if (!isAuthenticated()) return;

  const now = Date.now();
  const validMetrics = metricBuffer.filter((m) => now - m.timestamp < BUFFER_TTL_MS);

  logger.log(`[Performance] Flushing ${validMetrics.length} buffered metrics`);

  for (const metric of validMetrics) {
    writeMetricToFirestore(metric);
  }

  // Clear buffer and mark as flushed
  metricBuffer.length = 0;
  hasFlushed = true;
}

/**
 * Send metric to analytics/monitoring service
 */
function sendToAnalytics(metric: Metric) {
  // Store metric
  metrics[metric.name.toLowerCase() as keyof PerformanceMetrics] = metric.value;

  // Silently track metrics in development (no console spam)

  // Send to Firebase Performance Monitoring (if available)
  if (typeof window !== 'undefined' && 'performance' in window) {
    try {
      // Custom metric for Firebase Performance
      const customMetric: BufferedMetric = {
        name: metric.name,
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
        id: metric.id,
        timestamp: Date.now(),
      };

      // Store in sessionStorage for debugging
      sessionStorage.setItem(`perf_${metric.name}`, JSON.stringify(customMetric));

      // Log to Firestore in production (auth-gated)
      if (import.meta.env.PROD) {
        if (isAuthenticated()) {
          // User is authenticated, write directly
          writeMetricToFirestore(customMetric);
        } else {
          // Not authenticated yet, buffer the metric
          if (metricBuffer.length < MAX_BUFFER_SIZE) {
            metricBuffer.push(customMetric);
          }
          // Silently skip if buffer is full (don't error)
        }
      }

      // Send to analytics (Google Analytics, PostHog, etc.)
      if (window.gtag) {
        window.gtag('event', metric.name, {
          value: Math.round(metric.value),
          metric_rating: metric.rating,
          metric_delta: Math.round(metric.delta),
        });
      }
    } catch (error) {
      logger.error('[Performance] Failed to send metric:', error);
    }
  }
}

/**
 * Initialize performance monitoring
 */
export function initPerformanceMonitoring() {
  // Track Core Web Vitals
  onCLS(sendToAnalytics);
  onINP(sendToAnalytics); // INP replaces FID in web-vitals v3+
  onLCP(sendToAnalytics);
  onFCP(sendToAnalytics);
  onTTFB(sendToAnalytics);

  // Log initial page load time
  if (typeof window !== 'undefined' && window.performance) {
    window.addEventListener('load', () => {
      const perfData = window.performance.timing;
      const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;

      // Silently track page load time

      // Send custom metric (using unknown to bypass strict Metric type)
      sendToAnalytics({
        name: 'page_load',
        value: pageLoadTime,
        rating: pageLoadTime < 2000 ? 'good' : pageLoadTime < 4000 ? 'needs-improvement' : 'poor',
        delta: pageLoadTime,
        id: `page_load_${Date.now()}`,
        navigationType: 'navigate',
        entries: [],
      } as unknown as Metric);
    });
  }

  // Performance monitoring initialized silently
}

/**
 * Get current performance metrics
 */
export function getPerformanceMetrics(): PerformanceMetrics {
  return { ...metrics };
}

/**
 * Track custom performance metric
 */
export function trackCustomMetric(name: string, value: number) {
  const metric = {
    name,
    value,
    rating: 'good' as const,
    delta: value,
    id: `${name}_${Date.now()}`,
    navigationType: 'navigate' as const,
    entries: [],
  };

  sendToAnalytics(metric as unknown as Metric);
}

/**
 * Measure function execution time
 */
export function measurePerformance<T>(name: string, fn: () => T | Promise<T>): T | Promise<T> {
  const start = performance.now();

  const result = fn();

  if (result instanceof Promise) {
    return result.then((value) => {
      const duration = performance.now() - start;
      trackCustomMetric(name, duration);
      return value;
    });
  }

  const duration = performance.now() - start;
  trackCustomMetric(name, duration);
  return result;
}

/**
 * Create performance mark
 */
export function mark(name: string) {
  if (typeof window !== 'undefined' && window.performance) {
    performance.mark(name);
  }
}

/**
 * Measure between two marks
 */
export function measure(name: string, startMark: string, endMark: string) {
  if (typeof window !== 'undefined' && window.performance) {
    try {
      performance.measure(name, startMark, endMark);
      const measure = performance.getEntriesByName(name)[0];

      if (measure) {
        trackCustomMetric(name, measure.duration);
      }
    } catch (error) {
      logger.error('[Performance] Failed to measure:', error);
    }
  }
}

// Type augmentation for gtag
declare global {
  interface Window {
    gtag?: (command: string, eventName: string, params?: Record<string, unknown>) => void;
  }
}
